import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";

const PortalInbox = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationDetail, setConversationDetail] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const token = localStorage.getItem("portalToken");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchInbox = async () => {
    try {
      const response = await axios.get(`${API}/portal/messages/conversations`, { headers });
      setConversations(response.data || []);
    } catch {
      toast.error("Failed to load portal inbox");
    }
  };

  const openConversation = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/portal/messages/conversations/${conversationId}`, { headers });
      setSelectedConversation(conversationId);
      setConversationDetail(response.data);
      await axios.post(`${API}/portal/messages/conversations/${conversationId}/read`, {}, { headers });
      setConversations((current) => current.map((item) => item.id === conversationId ? { ...item, unread_count: 0 } : item));
    } catch {
      toast.error("Failed to open conversation");
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchInbox();
    axios.post(`${API}/portal/messages/presence`, {}, { headers }).catch(() => {});
    const interval = setInterval(() => {
      axios.post(`${API}/portal/messages/presence`, {}, { headers }).catch(() => {});
      if (selectedConversation) {
        openConversation(selectedConversation);
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [token, selectedConversation]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!selectedConversation || !newMessage.trim()) return;
    try {
      await axios.post(`${API}/portal/messages/conversations/${selectedConversation}/messages`, { body: newMessage.trim() }, { headers });
      setNewMessage("");
      await openConversation(selectedConversation);
      await fetchInbox();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Inbox</CardTitle>
          <CardDescription>Messages with tenant teams connected to your partner relationships.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {conversations.length > 0 ? conversations.map((conversation) => (
            <button key={conversation.id} onClick={() => openConversation(conversation.id)} className={`w-full rounded-xl border p-3 text-left ${selectedConversation === conversation.id ? "border-primary bg-primary/10" : "border-border/60"}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium">{conversation.title || conversation.participants.map((p) => p.full_name).filter(Boolean).join(", ")}</div>
                  <div className="text-sm text-muted-foreground mt-1">{conversation.latest_message?.body || "No messages yet"}</div>
                </div>
                {conversation.unread_count > 0 && <Badge>{conversation.unread_count}</Badge>}
              </div>
            </button>
          )) : <p className="text-sm text-muted-foreground">No messages yet.</p>}
        </CardContent>
      </Card>

      <Card className="xl:col-span-2 min-h-[520px]">
        <CardHeader>
          <CardTitle>{conversationDetail?.title || "Select a conversation"}</CardTitle>
          <CardDescription>{conversationDetail ? conversationDetail.participants.map((p) => p.full_name).filter(Boolean).join(", ") : "Choose a conversation to read and reply."}</CardDescription>
          {conversationDetail?.participants?.length > 0 && <div className="flex flex-wrap gap-2 mt-2">{conversationDetail.participants.map((p, idx) => <Badge key={`${p.full_name}-${idx}`} variant={p.is_online ? "default" : "secondary"}>{p.full_name || p.email || p.participant_type}{p.is_online ? " • online" : ""}</Badge>)}</div>}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="min-h-[360px] rounded-xl border border-border/60 bg-muted/20 p-4 space-y-3">
            {conversationDetail?.messages?.length > 0 ? conversationDetail.messages.map((message) => (
              <div key={message.id} className={`max-w-[80%] rounded-xl p-3 ${message.sender_type === "worker_identity" ? "ml-auto bg-primary text-primary-foreground" : "bg-card border border-border/60"}`}>
                <div className="text-xs opacity-80 mb-1">{message.sender_name || message.sender_type}</div>
                <div className="text-sm whitespace-pre-wrap">{message.body}</div>
                <div className="text-[11px] opacity-80 mt-2">{message.read_count > 0 ? `Read by ${message.read_count}` : "Sent"}</div>
              </div>
            )) : <div className="text-sm text-muted-foreground">No messages in this conversation yet.</div>}
          </div>
          <form onSubmit={handleSendMessage} className="flex gap-3">
            <Input value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Reply to the tenant team..." className="bg-muted/50" disabled={!selectedConversation} />
            <Button type="submit" disabled={!selectedConversation || !newMessage.trim()} className="bg-primary hover:bg-primary/90"><Send className="h-4 w-4" /></Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PortalInbox;
